// THE SIGN-IN SCREEN, module-owned (Law F1 — audit C-21, batch 21).
//
// Three portals used to carry this form as three 194–201-line copies that
// differed in six hunks: the heading, the subtitle, two field hints and one
// icon. The security-critical parts — the opaque failure, the step-up branch,
// the single-use human check — were identical, and three copies is where one
// of them quietly drifts (the same reasoning as ../step-up). Now the form lives
// here once and each portal is an ~11-line wire that passes its own copy.
//
// Failure is OPAQUE by design: never say which of email/password was wrong —
// the UI mirrors the API's opaque-denial discipline (auth-kit, ADR-000 §9).
// The failure is routed through Field's `error` slot (and aria-invalid on BOTH
// controls, so neither factor is singled out) rather than a paragraph floating
// under the form; #login-error is the polite announcement + describedby target.
//
// The automation ids (`form-login`, `input-login-email`, `input-login-password`,
// `btn-login-submit`, `link-forgot-password`) are the ones every portal's
// login test already asserts, so the move is invisible to the harness.
//
// `?next=` (the reading gate, ADR-010 §2.12): the member portal's reader sends
// an anonymous reader here as `/login?next=/read/<slug>`, and a sign-in that
// landed on "/" would strand them one click from the document they came for.
// The return path is read through the guarded `useNextPath` (../next.ts) —
// a same-origin path or nothing — so a link can never send a signed-in
// person off-site; without one, every portal's home is "/" as before.
import { Show, createSignal, type Component } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import { useSession } from "@erp/auth-kit/fe";
import { Dynamic } from "solid-js/web";
import { createTurnstile } from "@erp/ui/turnstile";
import { Button } from "@erp/ui/button";
import { Field, TextInput } from "@erp/ui/field";
import { PasswordInput } from "@erp/ui/password-input";
import { Card } from "@erp/ui/card";
import { BrandMark } from "@erp/ui/brand";
import { STEP_UP_REQUIRED, signInEmail } from "../index.js";
import { StepUpForm } from "../step-up/index.js";
import { nextOrHome, useNextPath } from "../next.js";

// ONE string, used by the visible field error and by the live announcement —
// it names both factors precisely so it can never leak which one failed.
const SIGN_IN_FAILED = "Sign-in failed — check your email and password.";
const ERROR_ID = "login-error";

export interface SignInScreenProps {
  /** The h1 beside the brand mark — the portal's name ("SHIPPRA ERP", "Library Desk"). */
  title: string;
  /** One line under the title saying who signs in here ("Sign in to the circulation desk."). */
  subtitle: string;
  /** The Email field's help text, in the portal's voice — which account this is, never a restated label. */
  emailHint: string;
  /** The Password field's help text — case sensitivity and where a reset comes from. */
  passwordHint: string;
  /** A lucide-solid COMPONENT rendered before the submit label (the desk and member portals show a LogIn glyph). */
  icon?: Component<{ size?: number; class?: string }>;
  /** Where the reset flow lives. Every portal mounts it at /reset-password today. */
  resetPath?: string;
}

/**
 * The email + password door, with the step-up second step and the human
 * check. A portal wires it from its depth-1 `screens/login.tsx` with its own
 * heading and hints; everything that must not drift between portals is here.
 */
export function SignInScreen(props: SignInScreenProps) {
  const navigate = useNavigate();
  const session = useSession();
  const next = useNextPath();
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [submitting, setSubmitting] = createSignal(false);
  const [failed, setFailed] = createSignal(false);
  /**
   * The second step (STEP-UP-POLICY.md), holding the server's own sentence.
   *
   * Non-null means the PASSWORD WAS RIGHT and a code is already in the person's
   * inbox — so the password form is replaced rather than annotated. Annotating
   * it would leave a "sign-in failed" shaped screen in front of someone whose
   * sign-in did not fail, which is how people conclude they mistyped and start
   * over, abandoning a code that is already valid.
   */
  const [stepUp, setStepUp] = createSignal<string | null>(null);
  /**
   * THE HUMAN CHECK. A sign-in is the form bots want most, so it carries the
   * widget and the button stays disabled until a token is in hand — nobody
   * presses a button that is going to be refused.
   */
  const captcha = createTurnstile({ action: "sign-in" });

  // Editing either factor retracts the verdict — a stale "invalid" on a field
  // the user is actively fixing is the classic false alarm.
  const clearFailure = () => {
    if (failed()) setFailed(false);
  };
  const invalid = () => (failed() ? "true" : undefined);
  const describedBy = () => (failed() ? ERROR_ID : undefined);

  const onSubmit = async (event: SubmitEvent) => {
    event.preventDefault();
    if (submitting()) return;
    setFailed(false);
    setSubmitting(true);
    try {
      // AuthResult contract: {ok, error?} — the fe wrappers never throw for
      // auth failures; only a network-level throw lands in catch.
      const result = await signInEmail(email(), password(), captcha.headers());
      // A Turnstile token is SINGLE-USE. Spending it after every attempt —
      // refused ones included — is what stops a second press replaying a token
      // the server has already redeemed and being refused for a reason the
      // person cannot act on.
      captcha.reset();
      if (!result.ok) {
        // A step-up is NOT a failure. The code is a machine value; the message
        // is human copy that will be rewritten, so the branch reads the code.
        if (result.code === STEP_UP_REQUIRED) {
          setStepUp(result.error ?? "A verification code has been sent to your email address.");
          return;
        }
        setFailed(true); // opaque: no distinction between bad email, bad password, or a 429
        return;
      }
      // Cookie is set — refetch claims through the provider (no hardcoded key),
      // then enter the app: the guarded `?next=` when there is one, else "/" —
      // the console, the desk, or the OPAC landing that now shows the member's
      // account link.
      await session.refetch();
      await navigate({ href: nextOrHome(next()) });
    } catch {
      // signInEmail/refetch are catch-safe; only navigate could throw here.
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main class="flex min-h-screen items-center justify-center bg-base-200 p-4">
      <Card padding="p-8" class="w-full max-w-sm">
        <div class="flex items-center gap-2.5">
          <BrandMark size={30} class="shrink-0" />
          <h1 class="text-2xl">{props.title}</h1>
        </div>
        <p class="mt-1 text-sm text-base-subtle">{props.subtitle}</p>
        {/* The password form and the second step are the SAME card, swapped —
            a person is in one moment of one task and a second screen would read
            as a second thing that went wrong. */}
        <Show
          when={stepUp() === null}
          fallback={
            <StepUpForm
              email={email()}
              reason={stepUp() ?? undefined}
              onVerified={async () => {
                // The session cookie came back on the verify response; the
                // provider re-reads claims exactly as it does after a password
                // sign-in, so nothing downstream knows which door was used.
                await session.refetch();
                await navigate({ href: nextOrHome(next()) });
              }}
              onCancel={() => {
                setStepUp(null);
                setPassword("");
              }}
            />
          }
        >
          <form id="form-login" class="mt-6 flex flex-col gap-2" onSubmit={(e) => void onSubmit(e)}>
            <Field label="Email" required hint={props.emailHint}>
              <TextInput
                id="input-login-email"
                // Field renders a <legend> (names the group, not the control);
                // aria-label names the input itself for AT (WCAG 1.3.1/4.1.2).
                aria-label="Email"
                type="email"
                autocomplete="email"
                autofocus
                required
                aria-invalid={invalid()}
                aria-describedby={describedBy()}
                value={email()}
                onInput={(e) => {
                  setEmail(e.currentTarget.value);
                  clearFailure();
                }}
              />
            </Field>
            {/* The verdict renders in the LAST field's error slot — adjacent to the
                submit button, so it reads as a form-level message, while both
                controls carry aria-invalid. */}
            <Field label="Password" required hint={props.passwordHint} error={failed() ? SIGN_IN_FAILED : undefined}>
              <PasswordInput
                id="input-login-password"
                aria-label="Password"
                autocomplete="current-password"
                required
                aria-invalid={invalid()}
                aria-describedby={describedBy()}
                value={password()}
                onInput={(e) => {
                  setPassword(e.currentTarget.value);
                  clearFailure();
                }}
              />
            </Field>
            {/* Announced on arrival and pointed at by aria-describedby; the visible
                copy already lives in the field above, so this stays off-screen
                instead of repeating itself. */}
            <Show when={failed()}>
              <p id={ERROR_ID} role="alert" class="sr-only">
                {SIGN_IN_FAILED}
              </p>
            </Show>
            <captcha.Widget class="mt-1" />
            <Button
              id="btn-login-submit"
              type="submit"
              variant="primary"
              loading={submitting()}
              disabled={!captcha.ready()}
              class="mt-2 w-full"
            >
              <Show when={props.icon}>{(icon) => <Dynamic component={icon()} size={15} aria-hidden="true" />}</Show>
              Sign in
            </Button>
            {/* The way IN to the reset flow. Without it the route exists and
                nobody finds it, which is the same as not having built it. Below
                the submit, not beside the password label: a person reaches for it
                after a failed attempt, not before their first. */}
            <a
              id="link-forgot-password"
              href={props.resetPath ?? "/reset-password"}
              class="mt-1 text-center text-detail-s text-base-subtle underline"
            >
              Forgotten your password?
            </a>
          </form>
        </Show>
      </Card>
    </main>
  );
}
